/**
 * WORKER LOG VIEWER
 *
 * Displays live worker log output in a monospace scrollable pane.
 * Auto-scrolls to bottom as new entries arrive.
 * Color-codes by level: info=muted, warn=yellow, error=red.
 */

import { useEffect, useRef } from "react";
import { useWorkerStore } from "@/stores/workerStore";
import { ScrollArea } from "@/components/ui/scroll-area";

const LEVEL_CLASSES: Record<string, string> = {
  info: "text-muted-foreground",
  warn: "text-yellow-500",
  error: "text-red-500",
};

export function WorkerLogViewer() {
  const logs = useWorkerStore((s) => s.logs);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs.length]);

  if (logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-xs text-muted-foreground">
        No log output yet
      </div>
    );
  }

  return (
    <ScrollArea className="h-48 rounded-md border bg-muted/30">
      <div className="p-2 font-mono text-[11px] leading-relaxed">
        {logs.map((log, i) => (
          <div key={i} className={LEVEL_CLASSES[log.level] ?? "text-muted-foreground"}>
            <span className="text-muted-foreground/50 mr-2 select-none">
              {new Date(log.timestamp).toLocaleTimeString()}
            </span>
            {log.message}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </ScrollArea>
  );
}
